import { useEffect, useState } from "react"

import { Box, Stack, Typography, useMediaQuery } from "@mui/material"
import { alpha, styled, useTheme } from "@mui/material/styles"
import { NumericFormat } from "react-number-format"

import { gsap, ScrollTrigger } from "@ggs/ui/local/gsap"
import { getPricePrefix } from "@ggs/utils"
import type { ButtonProps, FC, ResponsiveImageType } from ".."
import { Button, IconButton, SvgIcon, TripModal } from ".."

type StickyCTAContentProps = {
  id: string
  title: string
  image: ResponsiveImageType
  slug: string
  duration: string
  price: number
  currency: string
  primaryCountry: string
  destinationSlug: string
  savings?: string
  deadline?: string | null
}

const StickyCTA: FC<StickyCTAContentProps> = ({
  title,
  image,
  slug,
  primaryCountry,
  destinationSlug,
  duration,
  price,
  id,
  currency,
  savings,
  deadline,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: "#sticky-cta",
        start: "top 80%",
        end: "+=100",
        toggleActions: "play none none reverse",
      },
    })

    tl.from("#sticky-cta", { opacity: 0, y: 20, duration: 0.5 })

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => {
        trigger.kill()
      })
    }
  }, [])
  return (
    <>
      <Box
        id="sticky-cta"
        width="100%"
        display="flex"
        position="fixed"
        bottom={{ xs: 0, md: 40 }}
        justifyContent="center"
        height={{ xs: 94, md: "auto" }}
        boxShadow={{ xs: "0px -2px 3px 0px rgba(16, 24, 40, 0.10)", md: "none" }}
        bgcolor={(theme) => ({ xs: theme.palette["neutral.100"].main, md: "transparent" })}
        zIndex={3999}
      >
        {isMobile ? (
          <StickyCTAMobile
            duration={duration}
            price={price}
            currency={currency}
            savings={savings}
            onHandleClick={() => setIsModalOpen(true)}
            deadline={deadline}
          />
        ) : (
          <StickyCTAStyledWrapper mx={3}>
            <StickyCTATextContent
              title={title}
              price={price}
              duration={duration}
              currency={currency}
              savings={savings}
              deadline={deadline}
            />
            <StickyCTAButton onHandleClick={() => setIsModalOpen(true)}>
              Request a Quote
            </StickyCTAButton>
          </StickyCTAStyledWrapper>
        )}
      </Box>
      <TripModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        trip={{
          id,
          title,
          image,
          slug,
          primaryCountry,
          destinationSlug,
        }}
      />
    </>
  )
}

export default StickyCTA

/** A sub-component for the StickyCTA text */
const StickyCTATextContent: FC<
  Pick<StickyCTAContentProps, "title" | "price" | "duration" | "currency" | "savings" | "deadline">
> = ({ title, price, duration, currency, savings, deadline }) => {
  const prefix = getPricePrefix(currency)
  const renderSubtitle = () => (
    <Box display="flex" gap={1}>
      <Typography variant="body.sm" sx={(theme) => ({ color: theme.palette["neutral.200"].main })}>
        {`${duration} days -`}
      </Typography>
      {price < 200 ? (
        <Typography
          variant="body.sm"
          sx={(theme) => ({ color: theme.palette["neutral.200"].main })}
        >
          Please inquire
        </Typography>
      ) : (
        <Typography
          variant="body.sm"
          sx={(theme) => ({ color: theme.palette["neutral.200"].main })}
        >
          <NumericFormat
            value={price}
            displayType="text"
            thousandSeparator
            prefix={`From ${prefix}`}
            decimalScale={0}
          />
        </Typography>
      )}
      {savings && deadline && (
        <Typography
          variant="body.sm"
          fontWeight={500}
          sx={(theme) => ({ color: theme.palette["neutral.200"].main, marginLeft: 2 })}
        >
          {savings}
        </Typography>
      )}
    </Box>
  )
  return (
    <StyledTextContent p={4.5}>
      <Typography
        variant="body.md"
        component="h2"
        sx={(theme) => ({
          color: theme.palette["neutral.100"].main,
          fontWeight: 500,
        })}
      >
        {title}
      </Typography>
      {renderSubtitle()}
    </StyledTextContent>
  )
}

/** A sub-component for the StickyCTA button. */
const StickyCTAButton: FC<ButtonProps & { onHandleClick: () => void }> = ({
  onHandleClick,
  ...props
}) => (
  <>
    <Box
      sx={(theme) => ({
        padding: theme.spacing(3.75, 2.5),
        borderLeft: `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}`,
        [theme.breakpoints.down("md")]: {
          display: "none",
        },
      })}
    >
      <Button
        variant="default"
        sx={(theme) => ({ padding: theme.spacing(5, 7) })}
        onClick={onHandleClick}
        {...props}
      />
    </Box>
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: theme.spacing(13),
        borderLeft: `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}`,
        [theme.breakpoints.up("md")]: {
          display: "none",
        },
      })}
    >
      <IconButton
        variant="quaternary"
        onClick={onHandleClick}
        aria-label="Request a Quote"
        sx={{ background: "transparent", padding: 0 }}
        icon={
          <SvgIcon
            variant="arrowOutward"
            size={16}
            sx={{
              fill: (theme) => theme.palette["neutral.100"].main,
            }}
          />
        }
      />
    </Box>
  </>
)

/** styled components */
const StickyCTAStyledWrapper = styled(Box)(
  ({ theme }) => `
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  min-width: 944px;
  background: ${theme.palette["secondary.800"].main};
  box-shadow: 0px 0px 4px 0px rgba(48, 67, 105, 0.10);
  border: 1px solid ${alpha(theme.palette["neutral.100"].main, 0.2)};
  border-radius: ${theme.spacing(1.5)};
  z-index: 3999;
  bottom: ${theme.spacing(10)};
  ${theme.breakpoints.down(950)} {
    min-width: 366px;
    bottom: ${theme.spacing(3)}
  }
  `
)

const StyledTextContent = styled(Box)(
  ({ theme }) => `
  display: flex;
  justify-content: center;
  flex-direction: column;
  min-height: ${theme.spacing(16)};
  `
)

// a sub component for the mobile sticky cta
const StickyCTAMobile: FC<
  Pick<StickyCTAContentProps, "duration" | "price" | "currency" | "savings" | "deadline"> & {
    onHandleClick: () => void
  }
> = ({ duration, price, currency, savings, deadline, onHandleClick }) => {
  const prefix = getPricePrefix(currency)
  return (
    <Stack gap={1} width="100%" padding={(theme) => theme.spacing(2.5, 8)}>
      <Button
        variant="tertiary"
        onClick={onHandleClick}
        sx={{
          minHeight: "48px!important",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Typography variant="link.sm" textAlign="center">
          Request a Quote
        </Typography>
        <SvgIcon
          variant="arrowRight"
          strokeMode
          size={14}
          viewBox="0 0 17 16"
          sx={{
            fill: (theme) => theme.palette["neutral.100"].main,
            stroke: "none",
          }}
        />
      </Button>
      <Box display="flex" alignSelf="center">
        <Typography variant="body.md">{`${duration} days - `}</Typography>
        {price < 200 ? (
          <Typography variant="body.md" sx={{ ml: 1 }}>
            Please inquire
          </Typography>
        ) : (
          <Typography variant="body.md" pl={1}>
            <NumericFormat
              value={price}
              displayType="text"
              thousandSeparator
              prefix={`From ${prefix}`}
              decimalScale={0}
            />
          </Typography>
        )}
        {savings && deadline && (
          <Typography variant="body.md" fontWeight={500} sx={(theme) => ({ marginLeft: 2 })}>
            {savings}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}
